import { isTruthy } from '@ardoq/common-helpers';
import {
  DEFAULT_EMPTY_ITEM_LABELS,
  VOID_REPRESENTATION_DATA,
} from '@ardoq/global-consts';
import {
  ComponentGraphGroup,
  ComponentGraphNode,
  getUrlFieldValuesByComponentId,
  GraphInterface,
  GraphItemFormattingMaps,
  GraphItemsModel,
  ItemLabels,
  LabelsInterface,
  RepresentationInterface,
  SVGColorAttributes,
  TraversalAndGroupingResult,
  URLFieldValue,
} from '@ardoq/graph';
import { uniq } from 'lodash';
import {
  ConditionalFormattingState,
  RepresentationData,
} from '@ardoq/data-model';
import { GetRepresentationData } from 'tabview/relationshipsDiagramsViewModel/types';

export const getItems = (
  itemId: string,
  items: GraphItemsModel<ComponentGraphNode | ComponentGraphGroup>,
  rawGraph: TraversalAndGroupingResult
) => {
  const graphItem = items.byId.get(itemId);

  if (!graphItem) {
    return;
  }
  const isGroup = graphItem.isGroup();

  const rawGraphItemsMapIndex = isGroup ? graphItem.id : graphItem.modelId;
  const rawItemsMap = isGroup ? rawGraph.groupMap : rawGraph.componentMap;

  const rawGraphItem = rawItemsMap.get(rawGraphItemsMapIndex);

  return { graphItem, rawGraphItem };
};

export const getUpdatedItems = (
  items: GraphItemsModel<ComponentGraphNode | ComponentGraphGroup>,
  updatedModelIds: Set<string>
) =>
  new Set(
    [...items.add, ...items.update].filter(itemId => {
      const item = items.byId.get(itemId);
      return item && updatedModelIds.has(item.modelId);
    })
  );

export const getComponentUrlFieldValueEntries = (
  viewInterface: GraphInterface & LabelsInterface,
  updatedNodeIds: Set<string>,
  updatedGroupIds: Set<string>,
  nodes: GraphItemsModel<ComponentGraphNode>,
  groups: GraphItemsModel<ComponentGraphGroup>
): [string, URLFieldValue[] | null][] => {
  const allItemIds = [...updatedNodeIds, ...updatedGroupIds];

  const modelIds = uniq(
    allItemIds
      .map(itemId => {
        const graphItem = nodes.byId.get(itemId) ?? groups.byId.get(itemId);
        return graphItem?.modelId;
      })
      .filter(isTruthy)
  );

  const urlFieldValuesByModelId = getUrlFieldValuesByComponentId(
    viewInterface,
    modelIds
  );

  return [...urlFieldValuesByModelId.entries()];
};

export const getComponentRepresentationDataEntries = (
  updatedNodeIds: Set<string>,
  updatedGroupIds: Set<string>,
  nodes: GraphItemsModel<ComponentGraphNode>,
  groups: GraphItemsModel<ComponentGraphGroup>,
  getRepresentationData: GetRepresentationData
): [string, RepresentationData | null][] => {
  const modelIds = uniq(
    [...updatedNodeIds, ...updatedGroupIds]
      .map(itemId => {
        const graphItem = nodes.byId.get(itemId) ?? groups.byId.get(itemId);
        return graphItem?.isComponent() ? graphItem.modelId : null;
      })
      .filter(isTruthy)
  );

  return modelIds.map(modelId => [modelId, getRepresentationData(modelId)]);
};

export const getGraphItemColorEntries = (
  viewInterface: RepresentationInterface,
  updatedNodeIds: Set<string>,
  updatedGroupIds: Set<string>,
  nodes: GraphItemsModel<ComponentGraphNode>,
  groups: GraphItemsModel<ComponentGraphGroup>
): [string, SVGColorAttributes][] =>
  [...updatedNodeIds, ...updatedGroupIds]
    .map(itemId => nodes.byId.get(itemId) ?? groups.byId.get(itemId))
    .filter(isTruthy)
    .map(graphItem => [
      graphItem.id,
      viewInterface.getComponentDisplayColor(graphItem.modelId ?? '', {
        useAsBackgroundStyle: false,
      }),
    ]);

export const getGraphItemConditionalFormattingStateEntries = (
  viewInterface: RepresentationInterface,
  updatedNodeIds: Set<string>,
  updatedGroupIds: Set<string>,
  nodes: GraphItemsModel<ComponentGraphNode>,
  groups: GraphItemsModel<ComponentGraphGroup>
): [string, ConditionalFormattingState][] =>
  [...updatedNodeIds, ...updatedGroupIds]
    .map(itemId => nodes.byId.get(itemId) ?? groups.byId.get(itemId))
    .filter(isTruthy)
    .map(graphItem => [
      graphItem.id,
      graphItem.modelId && viewInterface.getConditionalFormattingState
        ? viewInterface.getConditionalFormattingState(graphItem.modelId)
        : ConditionalFormattingState.NORMAL,
    ]);

export type SetGraphItemPropertiesArgs = GraphItemFormattingMaps & {
  itemIds: string[];
  nodes: GraphItemsModel<ComponentGraphNode>;
  groups: GraphItemsModel<ComponentGraphGroup>;
  nodeLabelsData: Map<string, ItemLabels>;
  urlFieldValuesByComponentId: Map<string, URLFieldValue[] | null>;
  representationDataByComponentId: Map<string, RepresentationData | null>;
};

export const setGraphItemClassProperties = ({
  itemIds,
  nodes,
  groups,
  nodeLabelsData,
  urlFieldValuesByComponentId,
  representationDataByComponentId,
  nodeColorsByGraphItemId,
  conditionalFormattingState,
}: SetGraphItemPropertiesArgs): void => {
  itemIds.forEach(itemId => {
    const graphItem = nodes.byId.get(itemId) ?? groups.byId.get(itemId);

    if (!graphItem) return;

    const urlFieldValues =
      urlFieldValuesByComponentId.get(graphItem.modelId) ?? null;
    const itemLabels =
      nodeLabelsData.get(graphItem.id) ?? DEFAULT_EMPTY_ITEM_LABELS;

    const colorAttributes = nodeColorsByGraphItemId.get(graphItem.id) ?? {};

    const formattingState =
      conditionalFormattingState.get(graphItem.id) ??
      ConditionalFormattingState.NORMAL;

    const representationData = graphItem.isComponent()
      ? (representationDataByComponentId.get(graphItem.modelId) ??
        VOID_REPRESENTATION_DATA)
      : null;

    graphItem.setItemLabels(itemLabels);
    graphItem.setUrlFieldValues(urlFieldValues);
    graphItem.setRepresentationData(representationData);
    graphItem.setCssColorAttributes(colorAttributes);
    graphItem.setConditionalFormattingState(formattingState);
  });
};

export const addUpdated = <T extends ComponentGraphNode | ComponentGraphGroup>(
  items: GraphItemsModel<T>,
  updatedItemIds: Set<string>
) => ({
  ...items,
  add: items.add.filter(itemId => !updatedItemIds.has(itemId)),
  update: [...new Set([...items.update, ...updatedItemIds])],
});
